import { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../../common/Card';
import { Button } from '../../common/Button';
import { BarChartIcon } from '@radix-ui/react-icons';
import './ActivityFrequencyCard.css';

const ActivityFrequencyCard = ({ data }) => {
    const [selectedRange, setSelectedRange] = useState('7 Days');
    const timeRanges = ['7 Days', '30 Days', '3 Months', '12 Months'];

    const getDateLabel = (date, range) => {
        switch (range) {
            case '7 Days':
                // Show day name (e.g., "Mon", "Tue")
                return date.toLocaleDateString('en-US', { weekday: 'short' });
            case '30 Days':
                // Show day of month (e.g., "Mar 20")
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            case '3 Months':
            case '12 Months':
                // Show month name (e.g., "March")
                return date.toLocaleDateString('en-US', { month: 'short' });
            default:
                return '';
        }
    };

    const filteredData = useMemo(() => {
        const dataMap = new Map();
        const now = new Date();
        now.setHours(23, 59, 59, 999); // End of current day

        let daysToSubtract;
        let startDate = new Date(now);

        switch (selectedRange) {
            case '7 Days':
                daysToSubtract = 6; // Last 7 days including today
                startDate.setDate(startDate.getDate() - daysToSubtract);
                break;
            case '30 Days':
                daysToSubtract = 29; // Last 30 days including today
                startDate.setDate(startDate.getDate() - daysToSubtract);
                break;
            case '3 Months':
                startDate.setMonth(startDate.getMonth() - 2); // Last 3 months including current
                startDate.setDate(1);
                break;
            case '12 Months':
                startDate.setMonth(startDate.getMonth() - 11); // Last 12 months including current
                startDate.setDate(1);
                break;
            default:
                daysToSubtract = 6;
                startDate.setDate(startDate.getDate() - daysToSubtract);
        }

        // Initialize all dates in range with 0
        if (selectedRange === '12 Months' || selectedRange === '3 Months') {
            // For monthly view, initialize each month
            let currentDate = new Date(startDate);
            while (currentDate <= now) {
                const label = getDateLabel(currentDate, selectedRange);
                dataMap.set(label, 0);
                currentDate.setMonth(currentDate.getMonth() + 1);
            }
        } else {
            // For daily view, initialize each day
            let currentDate = new Date(startDate);
            while (currentDate <= now) {
                const label = getDateLabel(currentDate, selectedRange);
                dataMap.set(label, 0);
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        // Process the actual data
        if (data.stats) {
            data.stats.forEach(item => {
                const itemDate = new Date(item.date);
                if (itemDate >= startDate && itemDate <= now) {
                    const label = getDateLabel(itemDate, selectedRange);
                    dataMap.set(label, (dataMap.get(label) || 0) + item.count);
                }
            });
        }

        // Convert map to array format for chart
        return Array.from(dataMap).map(([name, count]) => ({
            name,
            count
        }));
    }, [selectedRange, data]);

    return (
        <Card>
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2 pb-2">
                <CardTitle className="flex items-center gap-2">
                    <BarChartIcon className="h-5 w-5" />
                    Activity Frequency
                </CardTitle>
                <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
                    {timeRanges.map(range => (
                        <Button
                            key={range}
                            variant={selectedRange === range ? "secondary" : "ghost"}
                            size="sm"
                            className="text-xs sm:text-sm w-full sm:w-auto"
                            onClick={() => setSelectedRange(range)}
                        >
                            {range}
                        </Button>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={filteredData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <XAxis 
                                dataKey="name" 
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            />
                            <YAxis 
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    background: 'hsl(var(--background))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: 'var(--radius)',
                                    boxShadow: 'var(--shadow)',
                                    color: 'hsl(var(--foreground))'
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="count"
                                stroke="hsl(var(--primary))"
                                strokeWidth={2}
                                fill="url(#colorCount)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default ActivityFrequencyCard; 